import { motion } from "framer-motion";
import GeneralButton from "../common/GeneralButton";
import { TiChevronLeft, TiChevronRight } from "react-icons/ti";
import {
  useStep,
  useDisclaimer,
  usePlan,
  useMealsSelection,
  useMealType,
  useChildDetails,
  useKid,
  useDate,
} from "../contexts/SubscriptionContext";
import Icon from "../common/Icon";
import { useToast } from "../contexts/ToastContext";
import { useEffect, useState } from "react";
import Modal from "../common/Modal";
import { useNavigate } from "react-router-dom";
import axios from "../../config/axios";
import { useCart } from "../contexts/CartContext";
import ContainerLoading from "../common/loading/ContainerLoading";
import FullScreenLoading from "../common/loading/FullScreenLoading";
import NoItemsFound from "../common/NoItemsFound";
import ApiErrorMessage from "../common/ApiErrorMessage";
import { useGeneral } from "../contexts/GeneralContext";
import { cleanHTML2, formatDateToReadable } from "../utils/commonHelper";
const usePageContent = () => {
  const [loading1, setLoading1] = useState(true);
  const [content, setContent] = useState([]);
  const [error, setError] = useState("");
  const { extraContent, setExtraContent } = useGeneral();

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const { data } = await axios.get(
          "/page-content/meal-subscription-discalimer",
        );
        if (active) setContent(data);
        setExtraContent((prev) => ({
          ...prev,
          socialChannels: data?.socialChannels,
          whatsAppNumber: data?.whatsAppNumber,
        }));
      } catch (err) {
        console.error(err);
        if (active) setError("Failed to load plans. Please try again later.");
      } finally {
        if (active) setLoading1(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return { content, loading1, error, extraContent };
};

const Disclaimer = () => {
  const { content, loading1, error, extraContent } = usePageContent();
  const { nextStep, prevStep } = useStep();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const { fetchCart } = useCart();
  const navigate = useNavigate();
  const { acceptedDisclaimer, acceptDisclaimer, resetDisclaimer } =
    useDisclaimer();
  const { addToast } = useToast();
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  const selectedChild = JSON.parse(localStorage.getItem("selectedChild"));
  const childId = selectedChild?._id;
  const { selectedPlan } = usePlan();
  const { selectedDate } = useDate();
  const { selectedKidId } = useKid();
  const { selectedMealType } = useMealType();
  const { selectedMeals } = useMealsSelection();
  const { childDetails } = useChildDetails();
  const toggleDisclaimer = () => {
    if (acceptedDisclaimer) {
      resetDisclaimer();
      localStorage.setItem("acceptedDisclaimer", "0");
    } else {
      acceptDisclaimer();
      localStorage.setItem("acceptedDisclaimer", "1");
    }
  };

  const handleNext = async () => {
    if (!acceptedDisclaimer) {
      setShow(true);
      return;
    }

    if (!selectedMeals || selectedMeals.length === 0) {
      addToast({
        message: "Please select at least one meal to continue.",
        type: "error",
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        customerId: loggedInUser?._id,
        planId: selectedPlan?._id,
        vegOrNonVeg: selectedMealType,
        customerSelectedStartDate: formatDateToReadable(selectedDate),
        mealIds: selectedMeals.map((meal) => meal._id),
        childId: childId || selectedKidId,
      };
      console.log(payload);
     
      const res = await axios.post(
        "/cart/add-meal-subscription-to-cart",
        payload,
      );
      if (res.status === 200 || res.status === 201) {
        setLoading(false);
        addToast("Meals added to cart!", "success");
        localStorage.setItem("mealCartId", res?.data?.cartId);
        await fetchCart();
        nextStep();
        navigate("/checkout/meals");
      }
    } catch (err) {
      setLoading(false);
      console.error("Failed to add subscription to cart:", err);
      addToast(
        "Failed to add subscription to cart. Please try again.",
        "error",
      );
    }
  };

  return (
    <>
      {loading1 && <FullScreenLoading />}
      {!loading1 && error ? (
        <ApiErrorMessage
          message1="Failed to load disclaimer"
          message2="Please check your connection and try again."
        />
      ) : !loading1 && !content ? (
        <NoItemsFound
          message1="Disclaimer unavailable"
          message2="Please check back later to continue."
        />
      ) : (
        <motion.div
          initial={{ x: -200, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -200, opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative"
        >
          <p className="text-34pt pb-4 lg:pb-9">
            {content?.heading || "Disclaimer"}
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_1fr] 2.5xl:grid-cols-[1.445fr_1fr] gap-8 items-start">
            <div>
              <div className="bg-[#DEE2DD] rounded-[15px] py-12 relative flex items-center justify-center ">
                {loading && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-[15px] z-10">
                    <ContainerLoading />
                  </div>
                )}

                <div className="w-full px-6 md:px-10 2.5xl:px-0 2.5xl:max-w-[804px]">
                  <div
                    className="font-mono text-green abouthtml small"
                    dangerouslySetInnerHTML={{
                      __html: cleanHTML2(content.content),
                    }}
                  />

                  {/* Checkbox-like row using icons */}
                  <div
                    className="flex items-center justify-start mt-10 cursor-pointer"
                    onClick={toggleDisclaimer}
                  >
                    <div className="w-[38px] h-[26px] me-5 lg:me-2">
                      <Icon
                        icon={acceptedDisclaimer ? "checked" : "checkbox"}
                        className="w-[30px] h-auto"
                      />
                    </div>
                    <p className="text-green text-18pt md:text-21pt select-none">
                      {extraContent?.mealSubscriptionMessages?.disclaimer ||
                        "I accept full responsibility as my child’s parent/guardian for using Nutricub"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col xs:flex-row gap-5 items-center justify-between mt-10">
                <GeneralButton
                  onClick={() => {
                    prevStep();
                    navigate(-1);
                  }}
                  text="Prev step"
                  title=""
                  iconPosition="Left"
                  bgColor="#ccc"
                  textColor="#000"
                  icon={<TiChevronLeft size={24} className="me-4" />}
                  className="text-24pt"
                />
                <GeneralButton
                  onClick={handleNext}
                  text="Next step"
                  title=""
                  bgColor="#30160E"
                  textColor="#ccc"
                  icon={<TiChevronRight size={24} className="ms-4" />}
                  className="text-24pt"
                />
              </div>
            </div>
          </div>

          <Modal
            isOpen={show}
            onClose={() => setShow(false)}
            title="Accept Disclaimer"
            onConfirm={() => setShow(false)}
            confirmButton="Ok"
            dismissible={false}
            showCancel={false}
          >
            <div className="font-mono">
              <p>You need to accept disclaimer to continue </p>
            </div>
          </Modal>
        </motion.div>
      )}
    </>
  );
};

export default Disclaimer;
